import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { LogoMark } from "./Logo";
import { MaskedLines, EASE } from "./Reveal";
import { useLenis, scrollToId } from "@/lib/lenis";

const useClock = (tz) => {
  const [t, setT] = useState("--:--:--");
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tz });
    const tick = () => setT(fmt.format(new Date()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [tz]);
  return t;
};

const Bridge = () => {
  const ist = useClock("Asia/Kolkata");
  const est = useClock("America/New_York");
  return (
    <motion.div
      data-testid="hero-bridge"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, delay: 1.6 }}
      className="mt-14 flex flex-wrap items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/50"
    >
      <div className="flex items-center gap-3">
        <span className="h-2 w-2 rounded-full bg-brand-cyan shadow-[0_0_14px_#4FC3E8]" />
        <span>Mumbai, IN</span>
        <span data-testid="hero-clock-ist" className="tabular-nums text-white">{ist}</span>
      </div>
      <svg className="h-3 min-w-[80px] flex-1" preserveAspectRatio="none" viewBox="0 0 200 12">
        <line x1="0" y1="6" x2="200" y2="6" stroke="#4FC3E8" strokeOpacity="0.55" strokeWidth="1.5" strokeDasharray="6 8" className="animate-dash" />
      </svg>
      <div className="flex items-center gap-3">
        <span data-testid="hero-clock-est" className="tabular-nums text-white">{est}</span>
        <span>New York, US</span>
        <span className="h-2 w-2 rounded-full bg-brand-corn shadow-[0_0_14px_#5271FF]" />
      </div>
    </motion.div>
  );
};

const Chip = ({ className, delay, z, label, sub, color, testId }) => (
  <motion.div
    data-testid={testId}
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1, delay, ease: EASE }}
    style={{ z }}
    className={`absolute ${className}`}
  >
    <div className="glass flex items-center gap-3 rounded-2xl px-4 py-3 animate-float" style={{ animationDelay: `${delay}s` }}>
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 16px ${color}` }} />
      <div>
        <div className="font-display text-sm font-semibold text-white">{label}</div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">{sub}</div>
      </div>
    </div>
  </motion.div>
);

const Visual = ({ mx, my }) => {
  const spring = { stiffness: 60, damping: 18, mass: 0.8 };
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), spring);
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-16, 16]), spring);
  return (
    <div className="relative [perspective:1400px]">
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative mx-auto aspect-square w-full max-w-[520px] [transform-style:preserve-3d]">
        <div className="absolute inset-0 rounded-full border border-white/10 [animation:spin_46s_linear_infinite]" style={{ borderTopColor: "rgba(79,195,232,.7)" }} />
        <div className="absolute inset-[11%] rounded-full border border-white/10 [animation:spin_30s_linear_infinite_reverse]" style={{ borderBottomColor: "rgba(82,113,255,.8)" }} />
        <div className="absolute inset-[24%] rounded-full bg-brand-indigo/40 blur-3xl" />
        <motion.div
          initial={{ scale: 0.55, opacity: 0, rotate: -40 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 1.5, delay: 0.5, ease: EASE }}
          style={{ z: 80 }}
          className="absolute inset-[21%]"
        >
          <LogoMark size="100%" className="text-white drop-shadow-[0_0_48px_rgba(79,195,232,.35)]" />
        </motion.div>
        <Chip testId="hero-chip-artwork360" className="left-[-4%] top-[16%]" delay={1.2} z={140} label="Artwork 360" sub="Creative & order ops" color="#5271FF" />
        <Chip testId="hero-chip-medlyn360" className="right-[-6%] bottom-[14%]" delay={1.4} z={120} label="Medlyn 360" sub="Live medical scribing" color="#4FC3E8" />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const lenis = useLenis();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-brand-navy text-white">
      <div className="hero-grid absolute inset-0" />
      <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-brand-indigo/40 blur-[140px]" />
      <div className="absolute -right-32 top-10 h-[420px] w-[420px] rounded-full bg-brand-cyan/15 blur-[140px]" />

      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-6 pb-24 pt-36 lg:grid-cols-12 lg:px-10 lg:pb-32 lg:pt-44">
        <motion.div style={{ y: yText, opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="mb-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-brand-cyan"
          >
            <span className="h-px w-10 bg-brand-cyan/60" />
            <span>Parent of Artwork 360 &amp; Medlyn 360</span>
          </motion.div>

          <MaskedLines
            data-testid="hero-title"
            delay={0.45}
            className="font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.02em] sm:text-5xl lg:text-6xl"
            lines={[
              "Precision outsourcing,",
              "human at the core,",
              <span key="a" className="text-brand-cyan">built in India.</span>,
            ]}
          />

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: EASE }}
            className="mt-8 max-w-xl font-body text-base leading-relaxed text-white/65 sm:text-lg"
          >
            Malansh Solutions Private Limited builds and manages remote teams for US businesses — creative production and
            order operations through Artwork 360, live human medical scribing through Medlyn 360 — recruited, trained and
            led from Mumbai, India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button data-testid="hero-cta-brands" onClick={() => scrollToId(lenis, "brands")} className="btn-primary">
              <span>Explore our brands</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="hero-cta-contact" onClick={() => scrollToId(lenis, "contact")} className="btn-ghost">
              <span>Start a conversation</span>
            </button>
          </motion.div>

          <Bridge />
        </motion.div>

        <motion.div style={{ y: yVisual }} className="lg:col-span-5">
          <Visual mx={mx} my={my} />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 lg:left-10"
      >
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        <span>Scroll</span>
      </motion.div>
    </section>
  );
};
